import { useState, useEffect } from "react";
import { Linkedin, Github, Download, Menu, X, CircleUser, Layers, Settings2, Mail, Calendar, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import ThemeToggle from "@/components/ThemeToggle";
interface HeaderProps {
  activeSection: string;
  onNavigate: (section: string) => void;
}
const navItems = [{
  id: "about",
  label: "About",
  Icon: CircleUser
}, {
  id: "consultation",
  label: "Consultation",
  Icon: Calendar
}, {
  id: "portfolio",
  label: "Works",
  Icon: Layers
}, {
  id: "services",
  label: "Services",
  Icon: Settings2
}, {
  id: "contact",
  label: "Contact",
  Icon: Mail
}];
const Header = ({
  activeSection,
  onNavigate
}: HeaderProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const handleNav = (id: string) => {
    onNavigate(id);
    setMobileOpen(false);
  };
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "h-14 bg-background/95 backdrop-blur-md shadow-sm border-b border-border" : "h-16 bg-background/80 backdrop-blur-sm"}`}>
      <div className="max-w-7xl mx-auto h-full px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        {/* Logo/Name */}
        

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
          {navItems.map(item => <button key={item.id} onClick={() => handleNav(item.id)} aria-label={`Navigate to ${item.label} section`} aria-current={activeSection === item.id ? "true" : undefined} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
                ${activeSection === item.id ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}
              `}>
              <item.Icon size={16} aria-hidden="true" />
              {item.label}
            </button>)}
        </nav>

        {/* Right Side: Social Icons + Theme + Download CV */}
        <div className="flex items-center gap-2">
          {/* Social Links - Hidden on mobile */}
          <div className="hidden md:flex items-center gap-1" role="navigation" aria-label="Social media links">
            <a href="https://www.linkedin.com/in/anyhakobyan/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-secondary/80 transition-colors" aria-label="View LinkedIn profile">
              <Linkedin size={16} aria-hidden="true" />
            </a>
            <a href="https://github.com/any-hakobyan" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-secondary/80 transition-colors" aria-label="View GitHub profile">
              <Github size={16} aria-hidden="true" />
            </a>
            <a href="https://www.upwork.com/freelancers/~018572473e001f6daa" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-secondary/80 transition-colors" aria-label="View Upwork profile">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M18.561 13.158c-1.102 0-2.135-.467-3.074-1.227l.228-1.076.008-.042c.207-1.143.849-3.06 2.839-3.06 1.492 0 2.703 1.212 2.703 2.703-.001 1.489-1.212 2.702-2.704 2.702zm0-8.14c-2.539 0-4.51 1.649-5.31 4.366-1.22-1.834-2.148-4.036-2.687-5.892H7.828v7.112c-.002 1.406-1.141 2.546-2.547 2.548-1.405-.002-2.543-1.143-2.545-2.548V3.492H0v7.112c0 2.914 2.37 5.303 5.281 5.303 2.913 0 5.283-2.389 5.283-5.303v-1.19c.529 1.107 1.182 2.229 1.974 3.221l-1.673 7.873h2.797l1.213-5.71c1.063.679 2.285 1.109 3.686 1.109 3 0 5.439-2.452 5.439-5.45 0-3-2.439-5.439-5.439-5.439z" />
              </svg>
            </a>
            <a href="https://t.me/AskAnyMarketing" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-secondary/80 transition-colors" aria-label="Contact on Telegram">
              <Send size={16} aria-hidden="true" />
            </a>
          </div>

          <ThemeToggle />

          {/* Download CV - Full button on desktop, icon on mobile */}
          <a href="/Any_Hakobyan_Resume.pdf" download aria-label="Download CV">
            <Button className="hidden sm:flex bg-primary text-primary-foreground hover:bg-primary/90" size="sm">
              <Download size={16} className="mr-2" aria-hidden="true" />
              Download CV
            </Button>
            <Button className="sm:hidden bg-primary text-primary-foreground hover:bg-primary/90" size="icon">
              <Download size={16} aria-hidden="true" />
            </Button>
          </a>

          {/* Mobile Menu Button */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2 rounded-lg bg-secondary text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors" aria-label="Open navigation menu">
                <Menu size={20} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] bg-card">
              <SheetTitle className="text-lg font-bold mb-6">Navigation</SheetTitle>
              
              {/* Mobile Navigation */}
              <nav className="flex flex-col gap-2" aria-label="Mobile navigation">
                {navItems.map(item => <button key={item.id} onClick={() => handleNav(item.id)} className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all
                      ${activeSection === item.id ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}
                    `}>
                    <item.Icon size={18} aria-hidden="true" />
                    {item.label}
                  </button>)}
              </nav>

              {/* Mobile Social Links */}
              <div className="mt-8 pt-6 border-t border-border">
                <p className="text-sm text-muted-foreground mb-4">Connect with me</p>
                <div className="flex items-center gap-2">
                  <a href="https://www.linkedin.com/in/anyhakobyan/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-secondary/80 transition-colors" aria-label="View LinkedIn profile">
                    <Linkedin size={18} aria-hidden="true" />
                  </a>
                  <a href="https://github.com/any-hakobyan" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-secondary/80 transition-colors" aria-label="View GitHub profile">
                    <Github size={18} aria-hidden="true" />
                  </a>
                  <a href="https://www.upwork.com/freelancers/~018572473e001f6daa" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-secondary/80 transition-colors" aria-label="View Upwork profile">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M18.561 13.158c-1.102 0-2.135-.467-3.074-1.227l.228-1.076.008-.042c.207-1.143.849-3.06 2.839-3.06 1.492 0 2.703 1.212 2.703 2.703-.001 1.489-1.212 2.702-2.704 2.702zm0-8.14c-2.539 0-4.51 1.649-5.31 4.366-1.22-1.834-2.148-4.036-2.687-5.892H7.828v7.112c-.002 1.406-1.141 2.546-2.547 2.548-1.405-.002-2.543-1.143-2.545-2.548V3.492H0v7.112c0 2.914 2.37 5.303 5.281 5.303 2.913 0 5.283-2.389 5.283-5.303v-1.19c.529 1.107 1.182 2.229 1.974 3.221l-1.673 7.873h2.797l1.213-5.71c1.063.679 2.285 1.109 3.686 1.109 3 0 5.439-2.452 5.439-5.45 0-3-2.439-5.439-5.439-5.439z" />
                    </svg>
                  </a>
                  <a href="https://t.me/AskAnyMarketing" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-secondary/80 transition-colors" aria-label="Contact on Telegram">
                    <Send size={18} aria-hidden="true" />
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>;
};
export default Header;